import { Injectable } from '@angular/core';

/*
 * Represents a localStorage store.
 */
@Injectable()
export class LocalStorage
{
    private readonly storage: Storage;  // Storage is a standard DOM Library class provided by TypeScript for working with Local Storage.

    constructor()
    {
        this.storage = this.getStorage();
    }

    /*
     * Safari, in Private Browsing Mode, looks like it supports localStorage but all calls to setItem
     * throw QuotaExceededError. We're going to detect this and just silently drop any calls to
     * setItem
     * to avoid the entire page breaking, without having to do a check at each usage of Storage.
     */
    private getStorage(): Storage
    {
        try
        {
            const storage = localStorage;
            storage.setItem('myID', '');
            storage.removeItem('myID');
            return storage;
        } catch (err)
        {
            return new AlternativeStorage();
        }
    }

    getItem(key: string): string | null
    {
        return this.storage.getItem('myID.' + key);
    }

    removeItem(key: string): void
    {
        this.storage.removeItem('myID.' + key);
    }

    /*
     * Sets an item in the storage.
     *
     * @param key - The key to set the value.
     * @param value - The value to set to the key.
     */
    setItem(key: string, data: string): void
    {
        this.storage.setItem('myID.' + key, data);
    }

}

/*
 * Represents an alternative storage for browser that doesn't support localstorage. (i.e. Safari in
 * private mode)
 * @implements Storage
 */
export class AlternativeStorage implements Storage
{
    private storageMap: any = {};

    /*
     * Returns an integer representing the number of data items stored in the storageMap object.
     */
    get length()
    {
        return Object.keys(this.storageMap).length;
    }

    /*
     * Remove all keys out of the storage.
     */
    clear(): void
    {
        this.storageMap = {};
    }

    /*
     * Return the key's value
     *
     * @param key - name of the key to retrieve the value of.
     * @return The key's value
     */
    getItem(key: string): string | null
    {
        if (typeof this.storageMap[key] !== 'undefined')
        {
            return this.storageMap[key];
        }
        return null;
    }

    /*
     * Return the nth key in the storage
     *
     * @param index - the number of the key you want to get the name of.
     * @return The name of the key.
     */
    key(index: number): string | null
    {
        return Object.keys(this.storageMap)[index] || null;
    }

    /*
     * Remove a key from the storage.
     *
     * @param key - the name of the key you want to remove.
     */
    removeItem(key: string): void
    {
        this.storageMap[key] = undefined;
    }

    /*
     * Add a key to the storage, or update a key's value if it already exists.
     *
     * @param key - the name of the key.
     * @param value - the value you want to give to the key.
     */
    setItem(key: string, value: string): void
    {
        this.storageMap[key] = value;
    }

    [key: string]: any;
    [index: number]: string;
}
